import { useCallback } from "react";
import { trpc } from "../api/trpc";

/**
 * Hook for fetching user settings (profile + preferences)
 */
export const useUserSettings = () => {
  return trpc.userSettings.getSettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for updating user profile
 */
export const useUpdateProfile = () => {
  const utils = trpc.useUtils();

  return trpc.userSettings.updateProfile.useMutation({
    onSuccess: () => {
      // Invalidate settings to refetch
      utils.userSettings.getSettings.invalidate();
      // Also invalidate auth.me since it returns user info
      utils.auth.me.invalidate();
    },
  });
};

/**
 * Hook for updating user preferences
 */
export const useUpdatePreferences = () => {
  const utils = trpc.useUtils();

  return trpc.userSettings.updatePreferences.useMutation({
    onSuccess: (_data, variables) => {
      // Invalidate settings to refetch
      utils.userSettings.getSettings.invalidate();

      // If theme was updated, also update localStorage for immediate effect
      if (variables.theme) {
        localStorage.setItem("theme", variables.theme);
        // Dispatch event to notify ThemeContext
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "theme",
            newValue: variables.theme,
          }),
        );
      }
    },
  });
};

/**
 * Hook for avatar upload with presigned URL flow
 */
export const useAvatarUpload = () => {
  const utils = trpc.useUtils();
  const initiateMutation = trpc.userSettings.initiateAvatarUpload.useMutation();
  const confirmMutation = trpc.userSettings.confirmAvatarUpload.useMutation({
    onSuccess: () => {
      // Invalidate settings to show new avatar
      utils.userSettings.getSettings.invalidate();
      utils.auth.me.invalidate();
    },
  });

  const uploadAvatar = useCallback(
    async (file: File) => {
      // 1. Get presigned URL
      const { uploadUrl, s3Key } = await initiateMutation.mutateAsync({
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });

      // 2. Upload directly to S3

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload avatar to S3");
      }

      // 3. Confirm upload to update user record
      const result = await confirmMutation.mutateAsync({ s3Key });

      return result.avatarUrl;
    },
    [initiateMutation, confirmMutation],
  );

  return {
    uploadAvatar,
    isUploading: initiateMutation.isPending || confirmMutation.isPending,
    error: initiateMutation.error || confirmMutation.error,
  };
};

/**
 * Hook for changing password
 */
export const useChangePassword = () => {
  return trpc.userSettings.changePassword.useMutation();
};

/**
 * Hook for deleting all chats
 */
export const useDeleteAllChats = () => {
  const utils = trpc.useUtils();

  return trpc.userSettings.deleteAllChats.useMutation({
    onSuccess: () => {
      // Invalidate chat-related queries
      utils.chat.getUserConversations.invalidate();
    },
  });
};

/**
 * Hook for deleting user account
 */
export const useDeleteAccount = () => {
  return trpc.userSettings.deleteAccount.useMutation();
};
